type NamedDocument = {filename?: string | null};

type FinalizedDocumentQuery = {
    /** Typname im Dateinamen, z.B. "Gutachten" oder "VereinfachteWertermittlung-24BelWertV-ausfuehrlich" */
    nameKey: string;
    externalOrderNumber: string | null | undefined;
    /** Abschlusszeitpunkt (ISO); zählt wie in reva-rest als UTC-Tag */
    finalizeDate: string | null | undefined;
    correctionCounter: number | null | undefined;
};

/**
 * Frühere Typnamen, unter denen Berichte archiviert sein können, neuere zuerst: der Stand von #19531 (nur
 * STAGE/TESTING) und der davor (reva-rest: `RevaPdfFilenameUtils.acceptedValuationPdfFilenames`). Solche Berichte
 * gelten weiter als vorhanden; nur exakt vergleichen, „VereinfachteWertermittlung-24BelWertV“ ist Präfix beider
 * aktuellen Typnamen (#19572).
 */
const LEGACY_NAME_KEYS: Readonly<Record<string, readonly string[]>> = {
    "VereinfachteWertermittlung-24BelWertV-ausfuehrlich": [
        "VereinfachteWertermittlung-24BelWertV",
        "VereinfachteWertermittlung",
    ],
    "VereinfachteWertermittlung-24BelWertV-kompakt": ["VereinfachteWertermittlung-kompakt", "Kompaktbewertung"],
};

/**
 * Dateiname des finalen Berichts nach dem Schema von reva-rest (`RevaPdfFilenameUtils`):
 * `<YYYY-MM-DD>_<identifier>_<TypName>[_Rev<n>].pdf` – ein finaler Bericht trägt kein Vorschau-Suffix
 * (`_Vorläufig`/`_Vorschau`), daher genügt der exakte Vergleich.
 */
export function finalizedDocumentFilename({
    nameKey,
    externalOrderNumber,
    finalizeDate,
    correctionCounter,
}: FinalizedDocumentQuery): string | null {
    const dateStr = utcDay(finalizeDate);
    if (!dateStr || !externalOrderNumber) return null;
    const correctionSuffix = correctionCounter ? `_Rev${correctionCounter}` : "";
    return `${dateStr}_${externalOrderNumber}_${nameKey}${correctionSuffix}.pdf`;
}

/** UTC-Tag (YYYY-MM-DD) eines ISO-Zeitpunkts – reva-rest datiert den Dateinamen in UTC. */
function utcDay(isoDateTime: string | null | undefined): string | null {
    if (!isoDateTime) return null;
    const timestamp = Date.parse(isoDateTime);
    return Number.isNaN(timestamp) ? null : new Date(timestamp).toISOString().substring(0, 10);
}

/**
 * Das archivierte finale Dokument zum Abschluss, oder null wenn es nicht eindeutig vorliegt. Gesucht wird unter
 * dem aktuellen Typnamen und danach unter den früheren (`LEGACY_NAME_KEYS`).
 */
export function findFinalizedDocument<T extends NamedDocument>(
    documents: T[] | null | undefined,
    query: FinalizedDocumentQuery,
): T | null {
    if (!documents) return null;
    const nameKeys = [query.nameKey, ...(LEGACY_NAME_KEYS[query.nameKey] ?? [])];
    for (const nameKey of nameKeys) {
        const filename = finalizedDocumentFilename({...query, nameKey});
        if (!filename) return null;
        const matches = documents.filter((doc) => doc.filename === filename);
        if (matches.length > 0) return matches.length === 1 ? matches[0] : null;
    }
    return null;
}
